import { type ReactNode, useCallback, useState } from "react";
import { View, type LayoutChangeEvent, type ViewStyle } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import type { MediaCarouselItem, MediaCarouselProps, MediaCarouselNavDirection } from "./MediaCarousel.types";
import { MediaCarouselNavButton, MediaCarouselThumbnailStrip } from "./parts";
import { useMediaCarousel } from "./useMediaCarousel";

const DEFAULT_MEDIA_ASPECT_RATIO = 16 / 9;
const DEFAULT_THUMBNAIL_WIDTH = 88;

function MediaCarouselInner<T extends MediaCarouselItem>(props: MediaCarouselProps<T>) {
  const {
    items,
    index: controlledIndex,
    defaultIndex,
    onIndexChange,
    loop = false,
    preloadCount = 1,
    renderMedia,
    renderDetail,
    renderOverlay,
    renderNavButton,
    renderEmpty,
    mediaAspectRatio = DEFAULT_MEDIA_ASPECT_RATIO,
    mediaHeight,
    mediaMaxHeight,
    fillHeight = false,
    showNavigation = true,
    enableKeyboardNavigation = true,
    accessibilityLabel = "Media carousel",
    previousLabel = "Previous item",
    nextLabel = "Next item",
    style,
    mediaStyle,
    detailStyle,
    testID,
  } = props;

  const {
    index,
    activeItem,
    itemCount,
    canGoPrevious,
    canGoNext,
    goToPrevious,
    goToNext,
    goToIndex,
    mountedItems,
    containerRef,
  } = useMediaCarousel<T>({
    items,
    index: controlledIndex,
    defaultIndex,
    onIndexChange,
    loop,
    preloadCount,
    enableKeyboardNavigation,
  });

  // Measured rather than derived from the window: the carousel is as likely to
  // sit in a split pane or beside a sidebar as it is to own the screen, and the
  // window overstates the room it actually has.
  const [frameSize, setFrameSize] = useState<Size | null>(null);
  const handleFrameLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setFrameSize((current) =>
      current !== null && current.width === width && current.height === height ? current : { width, height },
    );
  }, []);

  if (activeItem === undefined) {
    return (
      <View style={style} testID={testID}>
        {renderEmpty?.()}
      </View>
    );
  }

  const renderNavButtonFor = (direction: MediaCarouselNavDirection): ReactNode => {
    const isPrevious = direction === "previous";
    const onPress = isPrevious ? goToPrevious : goToNext;
    const disabled = isPrevious ? !canGoPrevious : !canGoNext;
    const defaultElement = (
      <MediaCarouselNavButton
        direction={direction}
        onPress={onPress}
        disabled={disabled}
        accessibilityLabel={isPrevious ? previousLabel : nextLabel}
      />
    );

    if (renderNavButton === undefined) return defaultElement;
    return renderNavButton({ direction, onPress, disabled, defaultElement });
  };

  const thumbnailPlacement = props.showThumbnails === true ? (props.thumbnailPlacement ?? "top") : "top";
  const thumbnailStrip =
    props.showThumbnails === true ?
      <MediaCarouselThumbnailStrip
        items={items}
        activeIndex={index}
        onSelect={goToIndex}
        renderThumbnail={props.renderThumbnail}
        thumbnailWidth={props.thumbnailWidth ?? DEFAULT_THUMBNAIL_WIDTH}
        thumbnailHeight={
          props.thumbnailHeight ?? Math.round((props.thumbnailWidth ?? DEFAULT_THUMBNAIL_WIDTH) / mediaAspectRatio)
        }
        style={[
          thumbnailPlacement === "top" ? styles.stripAboveMedia : styles.stripBelowMedia,
          props.thumbnailStripStyle,
        ]}
      />
    : null;

  const aspectRatio = activeItem.aspectRatio ?? mediaAspectRatio;
  const sizing = { fillHeight, mediaHeight, mediaMaxHeight, aspectRatio };
  const frameStyle = resolveFrameStyle(sizing);
  const stageStyle = resolveStageStyle({ ...sizing, frame: frameSize });

  // A tab stop only when the arrow keys have somewhere to go — a focusable
  // region that does nothing is just an extra stop on the way past.
  const keyboardNavigable = enableKeyboardNavigation && itemCount > 1;

  return (
    <View
      ref={containerRef}
      style={[styles.root, fillHeight && styles.rootFill, style]}
      testID={testID}
      role={keyboardNavigable ? "group" : undefined}
      accessibilityLabel={keyboardNavigable ? accessibilityLabel : undefined}
      tabIndex={keyboardNavigable ? 0 : undefined}
    >
      {thumbnailPlacement === "top" && thumbnailStrip}

      {/* The frame reserves the space; the stage is the media itself, shaped to
          the item's aspect ratio and centred in whatever the frame turned out
          to be. Overlays sit on the stage, not the frame, so a badge lands in
          the corner of the picture rather than out in the letterbox. */}
      <View style={[styles.frame, frameStyle]} onLayout={needsMeasuredFrame(sizing) ? handleFrameLayout : undefined}>
        <View
          style={[styles.stage, stageStyle, mediaStyle]}
          testID={testID === undefined ? undefined : `${testID}-media`}
        >
          {mountedItems.map(({ item, index: mountedIndex, isActive }) => (
            // Keyed by item id so a slot that stays mounted across a navigation
            // keeps its media element — and therefore its already-fetched image
            // or buffered video — instead of remounting and refetching.
            <View key={item.id} style={[styles.slot, !isActive && styles.slotHidden]} aria-hidden={!isActive}>
              {renderMedia({ item, index: mountedIndex, isActive })}
            </View>
          ))}

          <View style={styles.overlay}>
            {renderOverlay !== undefined && (
              <View style={styles.overlayContent}>
                {renderOverlay({
                  item: activeItem,
                  index,
                  itemCount,
                  goToPrevious,
                  goToNext,
                  canGoPrevious,
                  canGoNext,
                })}
              </View>
            )}

            {showNavigation && itemCount > 1 && (
              <View style={styles.navRow}>
                {renderNavButtonFor("previous")}
                {renderNavButtonFor("next")}
              </View>
            )}
          </View>
        </View>
      </View>

      {thumbnailPlacement === "bottom" && thumbnailStrip}

      {renderDetail !== undefined && (
        <View style={detailStyle}>{renderDetail({ item: activeItem, index, itemCount })}</View>
      )}
    </View>
  );
}

MediaCarouselInner.displayName = "MediaCarousel";

/**
 * Generic media carousel — a paged media area with a navigation overlay and an
 * optional detail area beneath it.
 *
 * The carousel owns navigation, layout and preloading; it renders no media
 * itself. Products supply `renderMedia` (snapshots, clips, anything else),
 * `renderDetail` for whatever belongs under the media, and `renderThumbnail`
 * when the jump-to-item strip is enabled.
 *
 * Only the active item and `preloadCount` neighbours on each side are ever
 * mounted, and the thumbnail strip is virtualised, so the number of media
 * elements alive at once stays bounded no matter how long `items` is.
 */
const MediaCarousel = MediaCarouselInner as <T extends MediaCarouselItem = MediaCarouselItem>(
  props: MediaCarouselProps<T>,
) => ReturnType<typeof MediaCarouselInner>;

export { MediaCarousel };

// ---------------------------------------------------------------------------
// Media sizing
// ---------------------------------------------------------------------------

interface Size {
  readonly width: number;
  readonly height: number;
}

interface FrameStyleOptions {
  readonly fillHeight: boolean;
  readonly mediaHeight: number | undefined;
  readonly mediaMaxHeight: number | undefined;
  readonly aspectRatio: number;
}

/**
 * How much room the media area claims. Only `fillHeight` takes its height from
 * the parent; the other modes derive it from the width the carousel is given,
 * which is what makes the default behaviour fluid across screen sizes.
 */
function resolveFrameStyle({ fillHeight, mediaHeight, mediaMaxHeight, aspectRatio }: FrameStyleOptions): ViewStyle {
  if (fillHeight) return { flex: 1, minHeight: 0 };
  if (mediaHeight != null) return { height: mediaHeight };
  if (mediaMaxHeight != null) return { aspectRatio, maxHeight: mediaMaxHeight };
  return { aspectRatio };
}

interface StageStyleOptions extends FrameStyleOptions {
  readonly frame: Size | null;
}

const FILLS_FRAME: ViewStyle = { width: "100%", height: "100%" };

/**
 * The largest box of the requested shape that fits the frame, centred by the
 * frame's own flex alignment.
 *
 * Deliberately never reads the *measured* frame height outside `fillHeight`.
 * A measurement describes the item that produced it, so paging to a taller
 * ratio would size the new item against the old item's shorter frame and flash
 * an undersized box until the next layout. Everywhere else the height the frame
 * can offer is knowable without measuring — it is a prop, or it follows from
 * the width, and neither changes with the ratio.
 */
function resolveStageStyle({
  fillHeight,
  mediaHeight,
  mediaMaxHeight,
  aspectRatio,
  frame,
}: StageStyleOptions): ViewStyle {
  // The frame already carries the ratio, so the stage is simply all of it.
  if (!needsMeasuredFrame({ fillHeight, mediaHeight, mediaMaxHeight })) return FILLS_FRAME;
  if (frame === null || frame.width <= 0) return FILLS_FRAME;

  const availableHeight = fillHeight ? frame.height : (mediaHeight ?? mediaMaxHeight);
  if (availableHeight == null || availableHeight <= 0) return FILLS_FRAME;

  const height = Math.min(availableHeight, frame.width / aspectRatio);
  return { width: height * aspectRatio, height };
}

/** Whether the stage has to be sized against a measurement at all. */
function needsMeasuredFrame({
  fillHeight,
  mediaHeight,
  mediaMaxHeight,
}: Pick<FrameStyleOptions, "fillHeight" | "mediaHeight" | "mediaMaxHeight">): boolean {
  return fillHeight || mediaHeight != null || mediaMaxHeight != null;
}

const styles = StyleSheet.create((theme) => ({
  root: {
    // Only the offset is set: the focus ring itself is left to the browser so
    // it keeps `:focus-visible` semantics — a ring for keyboard users, nothing
    // for a mouse click. A ring driven from onFocus state would show for both.
    outlineOffset: 2,
  },
  rootFill: {
    flex: 1,
    minHeight: 0,
  },
  stripAboveMedia: {
    marginBottom: theme.spacing.md,
  },
  stripBelowMedia: {
    marginTop: theme.spacing.md,
  },
  // Left transparent so the space around a letterboxed stage shows whatever the
  // carousel is sitting on rather than a slab of grey.
  frame: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  stage: {
    position: "relative",
    overflow: "hidden",
    borderRadius: theme.spacing.md,
    backgroundColor: theme.colors.borderCard,
  },
  slot: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  // Preloaded neighbours stay mounted but are invisible and inert.
  slotHidden: {
    opacity: 0,
    pointerEvents: "none",
  },
  overlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    pointerEvents: "box-none",
  },
  overlayContent: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    pointerEvents: "box-none",
  },
  navRow: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: theme.spacing.md,
    pointerEvents: "box-none",
  },
}));
